import React, { useState, useRef } from 'react'
import { Calendar } from 'lucide-react'
import { formatDate, formatDateFull, parseDateInput } from '@/lib/dateFormat'

export interface DateInputProps {
  id?: string
  name?: string
  value?: string // YYYY-MM-DD or empty
  onChange?: (value: string) => void
  min?: string // YYYY-MM-DD
  max?: string // YYYY-MM-DD
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  placeholder?: string
  className?: string
  error?: boolean | string
  title?: string
  'aria-label'?: string
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>
  formatFull?: boolean
}

export const DateInput: React.FC<DateInputProps> = ({
  id,
  name,
  value = '',
  onChange,
  min,
  max,
  disabled = false,
  readOnly = false,
  required = false,
  placeholder,
  className = '',
  error,
  title,
  'aria-label': ariaLabel,
  onPaste,
  formatFull = false,
}) => {
  const formatFn = formatFull ? formatDateFull : formatDate
  const [isFocused, setIsFocused] = useState(false)
  const [textValue, setTextValue] = useState(() => (value ? formatFn(value) : ''))
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const textInputRef = useRef<HTMLInputElement>(null)

  const commitValue = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) {
      if (value) {
        onChange?.('')
      }
      setTextValue('')
      return
    }

    const parsed = parseDateInput(trimmed)
    if (parsed) {
      // Valid calendar date parsed
      if (parsed !== value) {
        onChange?.(parsed)
      }
      setTextValue(formatFn(parsed))
    } else {
      // Revert to valid original value
      setTextValue(value ? formatFn(value) : '')
    }
  }

  const handleFocus = () => {
    if (disabled || readOnly) return
    setIsFocused(true)
    setTextValue(value ? formatFn(value) : '')
  }

  const handleBlur = () => {
    setIsFocused(false)
    commitValue(textValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitValue(textValue)
      textInputRef.current?.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setTextValue(value ? formatFn(value) : '')
      textInputRef.current?.blur()
    }
  }

  const handleCalendarClick = () => {
    if (disabled || readOnly) return
    const el = nativeInputRef.current
    if (!el) return
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker()
      } else {
        el.focus()
      }
    } catch {
      el.focus()
    }
  }

  const tooltipTitle = title ?? (value ? formatDateFull(value) : undefined)
  const hasError = Boolean(error)

  // Merge classes for container
  const baseContainerClass =
    'relative inline-flex items-center rounded border bg-white transition-colors ' +
    (hasError
      ? 'border-red-500 ring-1 ring-red-500'
      : 'border-slate-300 focus-within:border-emerald-600 focus-within:ring-1 focus-within:ring-emerald-600') +
    (disabled ? ' bg-slate-50 opacity-60 cursor-not-allowed' : '')

  return (
    <div
      className={`${baseContainerClass} ${className}`.trim()}
      title={tooltipTitle}
      style={{ minWidth: formatFull ? '8rem' : '6rem' }}
    >
      <input
        ref={textInputRef}
        id={id}
        name={name}
        type="text"
        value={isFocused ? textValue : value ? formatFn(value) : ''}
        placeholder={placeholder ?? (formatFull ? 'dd/mm/yyyy' : 'dd/mm')}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        aria-label={ariaLabel}
        onChange={(e) => setTextValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
        className="w-full min-w-0 bg-transparent px-2 py-1 text-xs sm:text-sm font-sans tabular-nums text-slate-800 outline-none placeholder:text-slate-400"
        style={{ fieldSizing: 'content' as any }}
      />

      {/* Hidden native date input to back the visual calendar picker */}
      <input
        ref={nativeInputRef}
        type="date"
        value={value || ''}
        min={min}
        max={max}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only absolute pointer-events-none opacity-0"
        onChange={(e) => {
          const val = e.target.value
          onChange?.(val)
          setTextValue(val ? formatFn(val) : '')
        }}
      />

      <button
        type="button"
        tabIndex={-1}
        disabled={disabled || readOnly}
        onClick={handleCalendarClick}
        aria-label="Chọn ngày"
        className="flex h-full items-center pr-2 pl-0.5 text-slate-400 hover:text-slate-600 focus:outline-none shrink-0"
      >
        <Calendar className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
